
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    if (!isHome) {
      window.REACT_APP_NAVIGATE?.('/', { state: { scrollTo: id } });
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-neutral-900/95 backdrop-blur-md shadow-lg border-b border-neutral-800' : 'bg-black/20 backdrop-blur-sm'
        }`}
      >
        <div className="w-full px-4 md:px-8 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-6">
              <Link to="/" className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/8671031128391c697a2e9f09b82e3420/b77abd256c0fcd5d12b7048a94ca1456.jpeg"
                    alt="Daro Tattoo Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xl md:text-2xl font-bold tracking-wider text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  DARO TATTOO
                </span>
              </Link>
              <a
                href="https://www.instagram.com/Daro_tattoo_"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex w-9 h-9 items-center justify-center bg-gradient-to-br from-amber-500 via-pink-500 to-purple-600 rounded-lg hover:scale-110 transition-transform cursor-pointer"
              >
                <i className="ri-instagram-line text-white text-lg"></i>
              </a>
            </div>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('inicio')} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-neutral-300 hover:text-white">Inicio</button>
              <button onClick={() => scrollToSection('artista')} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-neutral-300 hover:text-white">Artista</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-neutral-300 hover:text-white">Portfolio</button>
              <Link to="/galeria-arte-urbano" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${location.pathname === '/galeria-arte-urbano' ? 'text-amber-500' : 'text-neutral-300 hover:text-white'}`}>Galería</Link>
              <button onClick={() => scrollToSection('resenas')} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-neutral-300 hover:text-white">Reseñas</button>
              <Link to="/contacto" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${location.pathname === '/contacto' ? 'text-amber-500' : 'text-neutral-300 hover:text-white'}`}>Contacto</Link>
              <button
                onClick={() => scrollToSection('reservar')}
                className="px-6 py-2 bg-neutral-700 text-white text-sm font-medium hover:bg-neutral-600 transition-all cursor-pointer whitespace-nowrap border border-neutral-600"
                style={{ clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)' }}
              >
                <i className="ri-calendar-line mr-2"></i>
                RESERVAR AHORA
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
              aria-label="Menú"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-neutral-900/98 backdrop-blur-lg flex flex-col pt-20 px-6 pb-8 overflow-y-auto lg:hidden">
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Cerrar menú"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="flex flex-col gap-1 mt-4">
            {[
              { label: 'Inicio', id: 'inicio' },
              { label: 'Artista', id: 'artista' },
              { label: 'Portfolio', id: 'portfolio' },
              { label: 'Reseñas', id: 'resenas' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left text-2xl font-bold text-white py-4 border-b border-neutral-800 hover:text-amber-500 transition-colors cursor-pointer"
                style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/galeria-arte-urbano"
              onClick={() => setMobileOpen(false)}
              className={`text-left text-2xl font-bold py-4 border-b border-neutral-800 transition-colors cursor-pointer ${location.pathname === '/galeria-arte-urbano' ? 'text-amber-500' : 'text-white hover:text-amber-500'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
            >
              Galería
            </Link>
            <Link
              to="/contacto"
              onClick={() => setMobileOpen(false)}
              className={`text-left text-2xl font-bold py-4 border-b border-neutral-800 transition-colors cursor-pointer ${location.pathname === '/contacto' ? 'text-amber-500' : 'text-white hover:text-amber-500'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
            >
              Contacto
            </Link>
          </div>

          <div className="mt-8">
            <button
              onClick={() => scrollToSection('reservar')}
              className="w-full py-4 bg-amber-600 text-white text-lg font-bold uppercase tracking-wider hover:bg-amber-700 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Bebas Neue, sans-serif', clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)' }}
            >
              <i className="ri-calendar-line mr-2"></i>
              RESERVAR AHORA
            </button>
          </div>

          <div className="mt-auto pt-8 flex items-center justify-center gap-4">
            <a
              href="https://www.instagram.com/Daro_tattoo_"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 via-pink-500 to-purple-600 rounded-lg cursor-pointer"
            >
              <i className="ri-instagram-line text-white text-xl"></i>
            </a>
            <a
              href="https://wa.me/34658402605?text=%C2%A1Hola!%20Me%20gustar%C3%ADa%20consultar%20sobre%20un%20tatuaje%20en%20Daro%20Tattoo%20%F0%9F%8E%A8"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center bg-[#25D366] rounded-lg cursor-pointer hover:scale-110 transition-transform"
            >
              <i className="ri-whatsapp-line text-white text-xl"></i>
            </a>
          </div>
        </div>
      )}
    </>
  );
}
